/*
 * ESP32 Connection via Bluetooth Classic SPP + Python Bridge
 * 
 * Architecture:
 *   ESP32 (BT Classic SPP) --> COM port --> bt_bridge.py --> WebSocket --> This Web App
 * 
 * The Python bridge runs on the user's laptop at ws://localhost:8765
 * It reads the BT serial COM port and forwards JSON data via WebSocket.
 * 
 * This keeps the network stack clear for the Fakra Ethernet MCU connection.
 */

// Bridge WebSocket URL — runs on the user's local machine
const BRIDGE_WS_URL = 'ws://127.0.0.1:8765';

export const ESP32_CONFIG = {
  BRIDGE_URL: BRIDGE_WS_URL,
  DEVICE_NAME: 'TeslaDiag',
  PROTOCOL: 'Bluetooth Classic SPP',
};

// Command strings (sent as JSON over the bridge)
export const ESP32_COMMANDS = {
  PING: { cmd: 'ping' },
  STATUS: { cmd: 'status' },
  SCAN_DTC: { cmd: 'scan_dtc' },
  CLEAR_DTC: { cmd: 'clear_dtc' },
  GATEWAY_UNLOCK: { cmd: 'gateway' },
};

class ESP32BridgeManager {
  constructor() {
    this.ws = null;
    this.connected = false;
    this.reconnectTimer = null;
    this.dataCallback = null;
    this.statusCallback = null;
    this.responseCallback = null;
    this.disconnectCallback = null;
  }

  // Always "supported" since it's just a WebSocket to localhost
  static isSupported() {
    return true;
  }

  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve(true);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(BRIDGE_WS_URL);

        const timeout = setTimeout(() => {
          reject(new Error('Bridge not running. Start bt_bridge.py on your laptop first.'));
          if (this.ws) this.ws.close();
        }, 3000);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          this.connected = true;
          console.log('[ESP32] Bridge WebSocket connected');
          // Send ping to verify ESP32 is on the other end
          this.send(ESP32_COMMANDS.PING);
          resolve(true);
        };

        this.ws.onclose = () => {
          this.connected = false;
          console.log('[ESP32] Bridge disconnected');
          if (this.disconnectCallback) this.disconnectCallback();
          this._scheduleReconnect();
        };

        this.ws.onerror = (err) => {
          clearTimeout(timeout);
          console.warn('[ESP32] Bridge WebSocket error');
          reject(new Error('Cannot connect to bridge at ws://localhost:8765. Is bt_bridge.py running?'));
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            // Command response (has "cmd" field)
            if (data.cmd) {
              console.log('[ESP32] Response:', data);
              if (this.responseCallback) this.responseCallback(data);
              // Status responses
              if (data.cmd === 'status' && this.statusCallback) {
                this.statusCallback({
                  gateway_unlocked: data.gateway,
                  can: data.can,
                  service_mode: false,
                });
              }
              return;
            }

            // Error from bridge
            if (data.error) {
              console.warn('[ESP32] Bridge error:', data.error);
              return;
            }

            // Live vehicle data (has "v" field = pack voltage)
            if (data.v !== undefined) {
              if (this.dataCallback) this.dataCallback(data);
            }

          } catch (e) {
            console.warn('[ESP32] Parse error:', e);
          }
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
  }

  send(command) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[ESP32] Not connected to bridge');
      return false;
    }
    const msg = typeof command === 'string' ? command : JSON.stringify(command);
    this.ws.send(msg);
    return true;
  }

  // Convenience commands
  scanDTC() { return this.send(ESP32_COMMANDS.SCAN_DTC); }
  clearDTC() { return this.send(ESP32_COMMANDS.CLEAR_DTC); }
  unlockGateway() { return this.send(ESP32_COMMANDS.GATEWAY_UNLOCK); }
  requestStatus() { return this.send(ESP32_COMMANDS.STATUS); }

  sendODINRoutine(routineId) {
    return this.send({ cmd: 'odin', routine: routineId });
  }

  sendRawCAN(id, data) {
    return this.send({ cmd: 'raw', id, data });
  }

  // Callbacks
  onData(callback) { this.dataCallback = callback; }
  onStatus(callback) { this.statusCallback = callback; }
  onResponse(callback) { this.responseCallback = callback; }
  onDisconnect(callback) { this.disconnectCallback = callback; }

  _scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.connected) {
        this.connect().catch(() => {});
      }
    }, 5000);
  }
}

// Singleton
export const esp32Bridge = new ESP32BridgeManager();

// Re-export as bleManager for backward compatibility with existing components
export const bleManager = esp32Bridge;
export const BLEManager = ESP32BridgeManager;

export const BLE_COMMANDS = {
  SCAN_DTC: 0x01,
  CLEAR_DTC: 0x02,
  GATEWAY_UNLOCK: 0x10,
  ODIN_ROUTINE: 0x20,
};
